import { useAtomValue } from 'jotai';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import React, { useState } from 'react';
import {
    type Route,
    type SceneRendererProps,
    TabView,
} from 'react-native-tab-view';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    CreditCardIcon,
    H5Regular,
    Icon,
    isNonMobileWeb,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../common/components/GroveSimpleModal';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { adminUserAtom } from '../../entities/entityAtoms';
import type {
    MemberDetails,
    MembershipDetails,
} from '../../entities/entityTypes';
import { useRefetchMembershipDetailsMutationOptions } from '../../entities/membership/hooks/useRefetchMembershipDetailsMutationOptions';
import { useRemoveFromMembership } from '../hooks/useRemoveFromMembership';

const ChooseMember = ({
    membership,
    onChange,
}: {
    membership: MembershipDetails;
    onChange: (member: MemberDetails) => void;
}) => {
    const [selectedMember, setSelectedMember] = useState<MemberDetails | null>(
        null,
    );

    return (
        <YStack gap={16} flex={1}>
            <H5Regular>Select member to remove:</H5Regular>
            {membership.users.map((user) => {
                return (
                    <YStack key={user.id} gap={16}>
                        <XStack alignItems="center">
                            <Checkbox
                                checked={user.id === selectedMember?.id}
                                style={{
                                    marginRight: 8,
                                }}
                                onChange={(event) => {
                                    if (event.checked) {
                                        setSelectedMember(user);
                                    } else {
                                        setSelectedMember(null);
                                    }
                                }}
                            />
                            <BodyMediumRegular>
                                {user.firstName}{' '}
                            </BodyMediumRegular>
                            <BodyMediumRegular>
                                {user.lastName}{' '}
                            </BodyMediumRegular>
                            <BodyMediumRegular>
                                ({user.email})
                            </BodyMediumRegular>
                            {user.stripeCustomerId != null && (
                                <Icon
                                    color="$black"
                                    icon={CreditCardIcon}
                                    size={24}
                                    padding={4}
                                />
                            )}
                        </XStack>
                    </YStack>
                );
            })}
            <Button
                disabled={selectedMember === null}
                label="Next"
                size="small"
                onClick={() => {
                    if (selectedMember != null) {
                        onChange(selectedMember);
                    }
                }}
                style={{
                    width: '50%',
                }}
            />
        </YStack>
    );
};

const RemoveMember = ({
    member,
    onBack,
    onRemove,
}: {
    member: MemberDetails | null;
    onBack: () => void;
    onRemove: (member: MemberDetails) => void;
}) => {
    if (member == null) {
        return <BodyMediumRegular>Something went wrong!</BodyMediumRegular>;
    }
    return (
        <YStack gap={8} flex={1}>
            <XStack gap={8}>
                <BodyMediumEmphasized>Removing: </BodyMediumEmphasized>
                <BodyMediumRegular backgroundColor="$red400">
                    {member.firstName} {member.lastName}
                </BodyMediumRegular>
                <BodyMediumRegular>({member.email})</BodyMediumRegular>
            </XStack>
            <YStack>
                <BodyMediumEmphasized>This will:</BodyMediumEmphasized>
                <BodyMediumRegular>
                    1. Remove this member from the membership.
                </BodyMediumRegular>
                <BodyMediumRegular>
                    2. Remove this member from the sendbird chat.
                </BodyMediumRegular>
                <BodyMediumRegular>
                    3. Remove this member from stytch, rending them unable to
                    log in.
                </BodyMediumRegular>
                <Message
                    style={{ padding: 4 }}
                    severity="warn"
                    text="For legacy members, please remember to update billing in Stripe."
                ></Message>
                <BodyMediumRegular padding={4}>
                    Other steps necessary to fully remove a member (stripe, etc)
                    are not taken here!
                </BodyMediumRegular>
            </YStack>
            <BodyMediumEmphasized>
                Are you sure you want to do this?
            </BodyMediumEmphasized>
            <XStack gap={8} paddingTop={16}>
                <Button label="Back" size="small" onClick={onBack} />
                <Button
                    severity="danger"
                    label="Remove"
                    size="small"
                    onClick={() => {
                        onRemove(member);
                    }}
                />
            </XStack>
        </YStack>
    );
};

/*
 * A note here.  This removes the member from sendbird and stytch, and sets the
 * membership id to null in our db.  There may be other manual steps required to
 * fully remove a member from a membership that are not captured here.
 */
export const RemoveMemberFromMembershipModal = ({
    isOpen,
    onClose,
    membershipDetails,
}: {
    isOpen: boolean;
    onClose: () => void;
    membershipDetails: MembershipDetails;
}) => {
    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);

    const [selectedMember, setSelectedMember] = useState<MemberDetails | null>(
        null,
    );

    const { mutate: removeFromMembership } = useRemoveFromMembership();
    const options =
        useRefetchMembershipDetailsMutationOptions(membershipDetails);

    const [index, setIndex] = useState<number>(0);

    const routes: Route[] = [
        {
            key: 'choose',
            title: 'Choose Member',
        },
        {
            key: 'remove',
            title: 'Remove Member',
        },
    ];

    const renderScene = ({
        route,
        jumpTo,
    }: SceneRendererProps & { route: Route }) => {
        // Find the next screen to jump to
        if (route.key === 'choose') {
            return (
                <ChooseMember
                    onChange={(member: MemberDetails) => {
                        setSelectedMember(member);
                        jumpTo('remove');
                    }}
                    membership={membershipDetails}
                />
            );
        }

        if (route.key === 'remove') {
            return (
                <RemoveMember
                    member={selectedMember}
                    onBack={() => {
                        jumpTo('choose');
                    }}
                    onRemove={(member: MemberDetails) => {
                        try {
                            removeFromMembership(
                                {
                                    urlParams: {
                                        id: membershipDetails.id,
                                        userId: member.id,
                                    },
                                },
                                options,
                            );
                            toast.show(
                                `${member.firstName} ${
                                    member.lastName
                                } removed from membership!`,
                                MutationSuccessToastOptions,
                            );
                        } catch (e) {
                            toast.show(
                                'Could not remove from membership',
                                MutationErrorToastOptions(),
                            );
                        } finally {
                            setIndex(0);
                            onClose();
                        }
                    }}
                />
            );
        }

        return null;
    };

    if (
        !adminUser.authorization.selectedMembership.removeFromMembership.canUse
    ) {
        return null;
    }

    return (
        <GroveSimpleModal
            isOpen={isOpen}
            hasCloseButton={true}
            onClose={() => {
                setIndex(0);
                onClose();
            }}
            renderInAppRoot={false}
        >
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width={500}
                gap={24}
                marginBottom={24}
            >
                <YStack
                    flex={1}
                    $sm={{
                        alignSelf: 'center',
                    }}
                >
                    <TabView
                        style={{
                            width: isNonMobileWeb ? 500 : '100%',
                        }}
                        swipeEnabled={false}
                        navigationState={{ index, routes }}
                        onIndexChange={setIndex}
                        renderScene={renderScene}
                        renderTabBar={() => null}
                        animationEnabled={true}
                    />
                </YStack>
            </YStack>
        </GroveSimpleModal>
    );
};
